import {useEffect, useState} from 'react'
import {usePaymentConfig} from '../core/PaymentConfigProvider'
import {KTIcon} from '../../../_metronic/helpers'
import {ISelectPaymentOption} from '../../../coreGlobal/models/default'
import {getPaymentOption} from '../core/_request'
import {PageOverlayer} from '../../PageOverlayer'
import {formatMask} from '../../../utils/formatMonetaryToBr'
import Swal from 'sweetalert2'

const AUTOMATIC_PAYMENTS = [1, 2, 3, 4, 5]
const RECURRENCY_PAYMENT = 3

const methodSize = (length, index) => {
  if (index == 0 && length === 1) {
    return 'col-10 p-0'
  }

  if (index == 0 && length != 0) {
    return 'col-12 p-0'
  }

  if (index != 0 && index < length - 1) {
    return 'col-10 p-0'
  }

  return 'col-7 p-0'
}

const SwitchAction = ({position, length, addMethodPayment, removeMethodPayment}) => {
  if (position === 1 && length > 1) return <></>
  if (position == 1 && length == 1) {
    return (
      <div className='col-2 p-0 d-flex align-items-center justify-content-center '>
        <span
          onClick={() => addMethodPayment()}
          className='btn btn-light-danger font-weight-bold w-auto'
        >
          <KTIcon iconName='plus' className='svg-icon svg-icon-danger' />
        </span>
      </div>
    )
  }

  if (position !== 0 && position !== length) {
    return (
      <div className='col-2 p-0 d-flex align-items-center justify-content-center '>
        <span
          onClick={() => removeMethodPayment()}
          className='btn btn-light-danger font-weight-bold w-auto'
        >
          <KTIcon iconName='trash' className='svg-icon svg-icon-danger' />
        </span>
      </div>
    )
  }

  if (position !== 0 && position === length) {
    return (
      <>
        <div className='col-2 p-0 ms-2 me-2 d-flex align-items-center justify-content-center '>
          <span
            onClick={() => addMethodPayment()}
            className='btn btn-light-danger font-weight-bold w-auto'
          >
            <KTIcon iconName='plus' className='svg-icon svg-icon-danger' />
          </span>
        </div>
        <div className='col-2 p-0 d-flex align-items-center justify-content-center '>
          <span
            onClick={() => removeMethodPayment()}
            className='btn btn-light-danger font-weight-bold w-auto'
          >
            <KTIcon iconName='trash' className='svg-icon svg-icon-danger' />
          </span>
        </div>
      </>
    )
  }

  return <></>
}

interface SelectPaymentMethodProps {
  includeOldRecurrence?: boolean
  hideRecurrence?: boolean
  // Quando informado, a recorrência aceita de 1x até este limite
  recurrenceMaxInstallments?: number
}

export const SelectPaymentMethod = ({
  includeOldRecurrence,
  hideRecurrence,
  recurrenceMaxInstallments,
}: SelectPaymentMethodProps = {}) => {
  const [paymentOption, setStates] = useState<ISelectPaymentOption[] | undefined>([])
  const [loadingPage, setLoadingPage] = useState(false)

  const recurrenceMinInstallments = recurrenceMaxInstallments ? 1 : 2

  const buildInstallments = (option: ISelectPaymentOption) => {
    const recurrency = Number(option.value) === RECURRENCY_PAYMENT
    const minInstallment = recurrency ? recurrenceMinInstallments : 1
    const maxInstallment =
      recurrency && recurrenceMaxInstallments
        ? Math.min(recurrenceMaxInstallments, Number(option.numberInstallment))
        : Number(option.numberInstallment)

    return Array.from({length: maxInstallment - minInstallment + 1}, (_, i) => i + minInstallment)
  }

  const installmentsFor = (method: number) => {
    const option = paymentOption?.find((val) => Number(val.value) === Number(method))
    return option ? buildInstallments(option) : []
  }

  useEffect(() => {
    async function init() {
      try {
        setLoadingPage(true)
        const data = await getPaymentOption(includeOldRecurrence)
        setStates(
          hideRecurrence
            ? data?.filter((option) => Number(option.value) !== RECURRENCY_PAYMENT)
            : data
        )
      } catch (error: any) {
        await Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error?.response?.data?.message,
          timer: 2000,
        })
      } finally {
        setLoadingPage(false)
      }
    }

    init()
  }, [includeOldRecurrence, hideRecurrence])
  const {
    payment,
    setPayment,
    addMethodPayment,
    removeMethodPayment,
    handleChangeTypeMethodPayment,
    handleChangeValueMethodPayment,
    handleChangeInstallments,
  } = usePaymentConfig()

  useEffect(() => {
    if (!paymentOption?.length) return

    const available = paymentOption.map((option) => Number(option.value))
    if (payment.every((pay) => available.includes(Number(pay.method)))) return

    const firstOption =
      paymentOption.find((option) => AUTOMATIC_PAYMENTS.includes(Number(option.value))) ??
      paymentOption[0]
    const method = Number(firstOption.value)
    const recurrency = method === RECURRENCY_PAYMENT
    const installmentEnabled = Boolean(firstOption.installment_enabled)

    setPayment((prev) =>
      prev.map((pay) =>
        available.includes(Number(pay.method))
          ? pay
          : {
              ...pay,
              method,
              recurrency,
              hasInstallment: installmentEnabled,
              numberOfInstallments: recurrency ? recurrenceMinInstallments : 1,
            }
      )
    )
  }, [paymentOption, payment])

  const hasInstallment = (id, index, e) => {
    const data = paymentOption?.find((val) => {
      return val.value == id && val.installment_enabled
    })

    if (data !== undefined) {
      handleChangeTypeMethodPayment(index, e, true, recurrenceMinInstallments)
      return
    }

    handleChangeTypeMethodPayment(index, e, false, recurrenceMinInstallments)
  }

  return (
    <>
      {loadingPage ? (
        <PageOverlayer />
      ) : (
        payment?.map((pay, index) => {
          const paymentMethodSize = methodSize(payment.length, index)
          return (
            <div className='mb-8 w-100' key={`payment-${index}`}>
              <label>{index + 1}. Forma:</label>
              <div className='row m-0 col-12'>
                <div className={paymentMethodSize}>
                  <select
                    className='form-select form-select-lg form-control form-control-lg form-control-solid mb-3 mb-lg-0 border border-dark'
                    value={pay.method}
                    onChange={(e) => {
                      hasInstallment(e.target.value, index, e)
                    }}
                  >
                    {paymentOption?.some((option) =>
                      AUTOMATIC_PAYMENTS.includes(Number(option.value))
                    ) && (
                      <optgroup label='Automáticos:'>
                        {paymentOption
                          .filter((option) => AUTOMATIC_PAYMENTS.includes(Number(option.value)))
                          .map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {paymentOption?.some(
                      (option) => !AUTOMATIC_PAYMENTS.includes(Number(option.value))
                    ) && (
                      <optgroup label='Manuais:'>
                        {paymentOption
                          .filter((option) => !AUTOMATIC_PAYMENTS.includes(Number(option.value)))
                          .map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <SwitchAction
                  position={index + 1}
                  length={payment.length}
                  addMethodPayment={addMethodPayment}
                  removeMethodPayment={() => {
                    removeMethodPayment(pay.paymentRef)
                  }}
                />
              </div>
              <div className='row mt-2 m-0'>
                <div className={'col-8 p-0'}>
                  <label>Valor:</label>
                  <input
                    className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 border border-dark col-5'
                    value={formatMask(pay.value)}
                    onChange={(e) => {
                      handleChangeValueMethodPayment(index, formatMask(e.target.value))
                    }}
                  />
                </div>
                {pay.hasInstallment && (
                  <div className='col-4 p-0 ps-2'>
                    <label>N. Parcelas:</label>
                    <select
                      className='form-select form-select-lg form-control form-control-lg form-control-solid mb-3 mb-lg-0 border border-dark'
                      value={pay.numberOfInstallments}
                      onChange={(e) => handleChangeInstallments(index, e.target.value)}
                    >
                      {installmentsFor(pay.method).map((val) => (
                        <option key={`numberPlots-${pay.paymentRef}-${val}-${index}`} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )
        })
      )}
    </>
  )
}
